import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'motion/react';
import { AlertCircle, Check, X } from 'lucide-react';
import { STACKING } from '../lib/stacking';
import { reducedVariants, toastVariants } from '../motion/tokens';
import usePrefersReducedMotion from '../motion/usePrefersReducedMotion';

const ToastContext = createContext(null);

const TOAST_TTL_MS = 5500;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);
  const timersRef = useRef(new Map());
  const reduced = usePrefersReducedMotion();
  const variants = reducedVariants(toastVariants, reduced);

  const dismiss = useCallback((id) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message, type = 'success') => {
      const text = String(message || '').trim();
      if (!text) return;
      const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      setToasts((prev) => [...prev.slice(-4), { id, message: text, type }]);
      const timer = setTimeout(() => dismiss(id), TOAST_TTL_MS);
      timersRef.current.set(id, timer);
    },
    [dismiss]
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const value = useMemo(() => ({ showToast }), [showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {typeof document !== 'undefined' &&
        createPortal(
          <div
            className="pointer-events-none fixed inset-x-0 top-20 z-toast flex flex-col items-end gap-2 px-4 sm:px-6"
            style={{ zIndex: STACKING.toast }}
            aria-live="polite"
            aria-relevant="additions"
          >
            <AnimatePresence initial={false}>
              {toasts.map((toast) => (
                <motion.div
                  key={toast.id}
                  layout={!reduced}
                  role="status"
                  variants={variants}
                  initial="hidden"
                  animate="visible"
                  exit="exit"
                  className={`pointer-events-auto flex max-w-md items-start gap-2 rounded-lg px-4 py-3 text-sm text-white shadow-lg ${
                    toast.type === 'error' ? 'bg-red-600' : 'bg-emerald-600'
                  }`}
                >
                  {toast.type === 'error' ? (
                    <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" />
                  ) : (
                    <Check className="mt-0.5 h-5 w-5 shrink-0" />
                  )}
                  <span className="flex-1 leading-snug">{toast.message}</span>
                  <button
                    type="button"
                    onClick={() => dismiss(toast.id)}
                    className="rounded p-0.5 transition-colors hover:bg-white/15 active:scale-[0.97]"
                    aria-label="Dismiss"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>,
          document.body
        )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return {
      showToast: (message) => {
        if (typeof window !== 'undefined' && message) {
          window.alert(String(message));
        }
      },
    };
  }
  return ctx;
}
